<template>

  <Head title="Pegawai" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs :breadcrumbs="[
          { name: 'Dashboard', href: route('dashboard') },
          { name: 'Pegawai', href: route('pegawai.index') },
        ]" />

        <!-- Header Section -->
        <div class="mb-4 sm:mb-6">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">Data Pegawai</h3>
              <p class="mt-1 text-sm text-gray-600">
                Kelola data staff/pegawai jurusan di sini.
              </p>
            </div>
            <div class="flex items-center gap-4">
              <div class="text-sm text-gray-500">
                Total: {{ pegawais.total }} pegawai
              </div>
              <button v-if="$page.props.can.pegawai_edit"
                class="inline-flex items-center px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                @click="openAddModal">
                <i class="mr-2 fa-solid fa-plus"></i>
                Tambah Pegawai
              </button>
            </div>
          </div>
        </div>

        <!-- Search Input -->
        <div class="relative mb-4">
          <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <MagnifyingGlassVue class="w-4 h-4 text-gray-400" />
          </div>
          <input v-model="search" type="text" autocomplete="off" placeholder="Cari nama pegawai..." id="search"
            class="block w-full px-3 py-2.5 pl-10 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200" />
        </div>

        <!-- Table -->
        <div class="overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-300">
              <thead class="bg-gray-50">
                <tr>
                  <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                    #
                  </th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[150px]">
                    Nama
                  </th>
                  <th scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden md:table-cell min-w-[120px]">
                    NIP
                  </th>
                  <th scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden lg:table-cell min-w-[180px]">
                    Email
                  </th>
                  <th scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden sm:table-cell min-w-[120px]">
                    Jabatan
                  </th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[80px]"></th>
                  <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6 min-w-[100px]">
                    <span class="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <!-- Table Rows -->
                <tr v-for="(pegawai, index) in pegawais.data" :key="pegawai.id"
                  class="transition-colors duration-150 hover:bg-gray-50">
                  <td class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 align-top whitespace-nowrap sm:pl-6">
                    {{ getRowNumber(index) }}
                  </td>
                  <td class="px-3 py-4 text-sm align-top">
                    <div class="flex items-center">
                      <div class="flex-shrink-0 w-10 h-10">
                        <img v-if="pegawai.foto" :src="pegawai.foto.startsWith('http')
                          ? pegawai.foto
                          : '/storage/' + pegawai.foto
                          " alt="Foto Pegawai" class="object-cover w-10 h-10 border rounded-full" />
                        <div v-else class="flex items-center justify-center w-10 h-10 bg-gray-200 rounded-full">
                          <i class="text-gray-400 fa-solid fa-user"></i>
                        </div>
                      </div>
                      <div class="ml-4">
                        <div class="font-medium text-gray-900">
                          {{ pegawai.nama }}
                        </div>
                        <div class="text-gray-500 sm:hidden">
                          {{ pegawai.nip }}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="hidden px-3 py-4 text-sm text-gray-900 align-top md:table-cell">
                    {{ pegawai.nip }}
                  </td>
                  <td class="hidden px-3 py-4 text-sm text-gray-900 align-top lg:table-cell">
                    <div class="max-w-xs truncate">
                      {{ pegawai.email }}
                    </div>
                  </td>
                  <td class="hidden px-3 py-4 text-sm text-gray-900 align-top sm:table-cell">
                    <span class="inline-flex px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
                      {{ pegawai.jabatan?.name || "-" }}
                    </span>
                  </td>
                  <td class="px-3 py-4 text-sm text-gray-900 align-top sm:hidden">
                    <img v-if="pegawai.foto" :src="pegawai.foto.startsWith('http')
                      ? pegawai.foto
                      : '/storage/' + pegawai.foto
                      " alt="Foto Pegawai" class="object-cover w-8 h-8 border rounded-full" />
                    <div v-else class="flex items-center justify-center w-8 h-8 bg-gray-200 rounded-full">
                      <i class="text-xs text-gray-400 fa-solid fa-user"></i>
                    </div>
                  </td>
                  <td class="relative py-4 pl-3 pr-4 text-sm font-medium text-right align-top sm:pr-6">
                    <div class="flex items-center justify-end space-x-1 sm:space-x-2">
                      <!-- View/Edit Button -->
                      <Link :href="route('pegawai.detail', pegawai.idpegawai)"
                        class="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-100 rounded-md hover:bg-indigo-200 hover:text-indigo-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                        title="Detail">
                      <i class="w-3 h-3 fa-solid fa-eye"></i>
                      <span class="hidden ml-1 sm:inline">Detail</span>
                      </Link>
                      <!-- Delete Button -->
                      <button v-if="$page.props.can.pegawai_delete" @click="deletePegawai(pegawai.idpegawai)"
                        class="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 hover:text-red-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                        title="Hapus">
                        <i class="w-3 h-3 fa-solid fa-trash"></i>
                        <span class="hidden ml-1 sm:inline">Hapus</span>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
            <Pagination :data="pegawais" :updatedPageNumber="updatedPageNumber" />
          </div>
        </div>

        <ModalPegawai v-model:isOpen="isModalOpen" :title="'Tambah Pegawai'" :fakultas="props.faculty"
          :jabatan="props.jabatan" />
      </div>
    </template>
  </Container>
</template>

<script setup>
import { useForm, usePage, router } from "@inertiajs/vue3";
import { ref, computed, watch } from "vue";

import Pagination from "@/Components/Pagination.vue";
import MagnifyingGlassVue from "@/Components/MagnifyingGlass.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import Header from "@/Components/Header.vue";
import Container from "@/Layouts/Container.vue";
import ModalPegawai from "@/Components/ModalPegawai.vue";
import Swal from "sweetalert2";
import { Link } from "@inertiajs/vue3";

const isModalOpen = ref(false);
const props = defineProps({
  pegawais: {
    type: Object,
    required: true,
  },
  faculty: {
    type: Object,
    required: true,
  },
  jabatan: {
    type: Object,
    required: true,
  },
});

const openAddModal = () => {
  isModalOpen.value = true;
};

/**
 * @param pagination
 */

let search = ref(usePage().props.search || ""),
  pageNumber = ref(1);

let pegawaiUrl = computed(() => {
  let url = new URL(route("pegawai.index"));
  url.searchParams.set("page", pageNumber.value); // Use 'set' to replace existing params
  if (search.value) {
    url.searchParams.set("search", search.value); // Use 'set' for search
  }

  return url;
});

const updatedPageNumber = (link) => {
  if (link && link.url) {
    try {
      const urlParams = new URLSearchParams(link.url.split("?")[1] || "");
      const page = urlParams.get("page");
      if (page) {
        pageNumber.value = parseInt(page);
      }
    } catch (error) {
      console.error("Error parsing pagination URL:", error);
    }
  }
};

watch(
  () => pegawaiUrl.value,
  (updatedPegawaiUrl) => {
    router.visit(updatedPegawaiUrl.toString(), {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    });
  }
);

watch(
  () => search.value,
  (value) => {
    if (value) {
      pageNumber.value = 1; // Reset to page 1 when search is updated
    }
  }
);

const deleteForm = useForm({});

// Computed property for row number calculation with fallbacks
const getRowNumber = computed(() => {
  return (index) => {
    const currentPage = props.pegawais?.current_page || 1;
    const perPage = props.pegawais?.per_page || 10;
    return (currentPage - 1) * perPage + index + 1;
  };
});

const deletePegawai = (pegawaiId) => {
  Swal.fire({
    title: "Apakah Anda yakin?",
    text: "Data pegawai ini akan dihapus secara permanen!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#dc2626",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, hapus!",
    cancelButtonText: "Batal",
  }).then((result) => {
    if (result.isConfirmed) {
      deleteForm.delete(route("pegawai.destroy", pegawaiId), {
        onError: () => {
          Swal.fire({
            title: "Gagal!",
            text: "Terjadi kesalahan saat menghapus data pegawai.",
            icon: "error",
            confirmButtonText: "OK",
          });
        },
      });
    }
  });
};
</script>
